//#region imports

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";

import { Map, ZoomControl, GeoJson, Overlay, Marker } from "pigeon-maps";

import { Colors, Spacing } from "_styles";
import { BsArrowCounterclockwise } from "react-icons/bs";
import { Subtitle } from "@tremor/react";
import { FaToggleOn, FaToggleOff, FaRegCompass } from "react-icons/fa";

import './styles.css';

//#endregion

//#region constant
const DEFAULT_RADIUS = 50;

const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);

const toNumberOrNull = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

// map providers 
const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

const LABELS_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/${z}/${y}/${x}`;

const OSM_PROVIDER = (x, y, z) =>
    `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

//#endregion

const buildPolygonGeoJson = (points) => {
    if (!Array.isArray(points) || points.length < 3) return null;
    const closed = [...points, points[0]];
    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                geometry: {
                    type: "Polygon",
                    coordinates: [closed.map(([lat, lng]) => [lng, lat])]
                },
                properties: {}
            }
        ]
    };
};

const makeCircleGeoJson = ([lat, lng], radiusMeters = DEFAULT_RADIUS, points = 64) => {
    const coords = [];
    const earthRadius = 6371000;
    const toRad = Math.PI / 180;
    const toDeg = 180 / Math.PI;

    const lat1 = lat * toRad;
    const lng1 = lng * toRad;

    for (let i = 0; i <= points; i++) {
        const bearing = (2 * Math.PI * i) / points;
        const lat2 = Math.asin(
            Math.sin(lat1) * Math.cos(radiusMeters / earthRadius) +
            Math.cos(lat1) * Math.sin(radiusMeters / earthRadius) * Math.cos(bearing)
        );
        const lng2 =
            lng1 +
            Math.atan2(
                Math.sin(bearing) * Math.sin(radiusMeters / earthRadius) * Math.cos(lat1),
                Math.cos(radiusMeters / earthRadius) - Math.sin(lat1) * Math.sin(lat2)
            );

        coords.push([lng2 * toDeg, lat2 * toDeg]);
    }

    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [coords] },
            properties: {}
        }]
    };
};

const LiveLocationMap = (props) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const airportCoordinates = useSelector(SelectCurrentCoordinates);

    const {
        geofence,
        setGeofence,
        polygon,
        setPolygon,
        geofenceType,
        setGeofenceType,
        circularGeofence,
        setCircularGeofence
    } = props;

    const [mapCenter, setMapCenter] = useState([0, 0]);
    const [zoom, setZoom] = useState(18);
    const [satelliteView, setSatelliteView] = useState(true);

    // local UI radius (mirrors prop radius while allowing typing)
    const [geofenceRadius, setGeofenceRadius] = useState(DEFAULT_RADIUS);

    // fetch airport coords once (authority change safe)
    useEffect(() => {
        if (!dbUser?.details?.authority) return;
        dispatch(getAirportUuidAndCoordinates(dbUser.details.authority));
    }, [dispatch, dbUser?.details?.authority]);

    // center map to airport only when no geofence type selected
    useEffect(() => {
        if (!airportCoordinates?.lat || !airportCoordinates?.lon) return;
        if (geofenceType) return;
        setMapCenter([Number(airportCoordinates.lat), Number(airportCoordinates.lon)]);
    }, [airportCoordinates?.lat, airportCoordinates?.lon, geofenceType]);

    // when switching to circular, only default radius if missing
    useEffect(() => {
        if (geofenceType !== 'circular') return;

        const r = circularGeofence?.radius;
        if (isFiniteNum(r) && r > 0) {
            setGeofenceRadius(r);
            return;
        }

        setGeofenceRadius(DEFAULT_RADIUS);
        setCircularGeofence((prev) => ({ ...(prev || {}), radius: DEFAULT_RADIUS }));
    }, [geofenceType]);

    // keep local radius in sync when parent radius changes
    useEffect(() => {
        if (geofenceType !== 'circular') return;
        const r = circularGeofence?.radius;
        if (isFiniteNum(r) && r > 0 && r !== geofenceRadius) {
            setGeofenceRadius(r);
        }
    }, [geofenceType, circularGeofence?.radius]);

    // build geofence geojson
    useEffect(() => {
        if (!geofenceType) return;

        if (geofenceType === "polygon") {
            const gj = buildPolygonGeoJson(polygon);
            setGeofence(gj);
            if (gj && Array.isArray(polygon) && polygon.length) {
                setMapCenter([Number(polygon[0][0]), Number(polygon[0][1])]);
            }
            return;
        }

        if (geofenceType === "circular") {
            const { lat, long, radius } = circularGeofence || {};
            if (isFiniteNum(lat) && isFiniteNum(long) && isFiniteNum(radius) && radius > 0) {
                setGeofence(makeCircleGeoJson([lat, long], radius));
                setMapCenter([Number(lat), Number(long)]);
            } else {
                setGeofence(null);
            }
        }
    }, [geofenceType, polygon, circularGeofence, setGeofence]);

    //#region handlers
    const provider = useMemo(
        () => (satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER),
        [satelliteView]
    );

    const handleRecenter = () => {
        if (geofenceType === 'circular' && isFiniteNum(circularGeofence?.lat) && isFiniteNum(circularGeofence?.long)) {
            setMapCenter([Number(circularGeofence.lat), Number(circularGeofence.long)]);
        } else if (geofenceType === 'polygon' && Array.isArray(polygon) && polygon.length) {
            setMapCenter([Number(polygon[0][0]), Number(polygon[0][1])]);
        } else if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([Number(airportCoordinates.lat), Number(airportCoordinates.lon)]);
        }
        setZoom(18);
    };

    const handleClick = ({ latLng }) => {
        if (!geofenceType) {
            alert('Please select geofence type');
            return;
        }

        if (geofenceType === 'polygon') {
            setPolygon((prev) => [...prev, latLng]);
            return;
        }

        if (geofenceType === 'circular') {
            const [lat, lng] = latLng;
            setCircularGeofence({
                lat,
                long: lng,
                radius: isFiniteNum(geofenceRadius) ? geofenceRadius : DEFAULT_RADIUS,
            });
        }
    };

    const handleMapView = () => setSatelliteView((prev) => !prev);

    const clearMap = () => {
        setGeofence(null);
        setPolygon([]);
        setGeofenceRadius(0);
        setCircularGeofence({ lat: null, long: null, radius: 0 });
        setGeofenceType(null);
    };

    //#endregion

    return (
        <div id='mapParent' style={{ border: `4px solid  ${Colors.PRM_BLUE}` }}>
            <div className='p-3 d-flex flex-column gap-2 bg-light'>
                <Subtitle>Please select the type of geofence</Subtitle>
                <div className='d-flex gap-2'>
                    <label>
                        <input
                            type="radio"
                            name="geofenceType"
                            value="circular"
                            checked={geofenceType === 'circular'}
                            onChange={(e) => setGeofenceType(e.target.value)}
                        />{" "}
                        Circular
                    </label>

                    <label>
                        <input
                            type="radio"
                            name="geofenceType"
                            value="polygon"
                            checked={geofenceType === 'polygon'}
                            onChange={(e) => setGeofenceType(e.target.value)}
                        />{" "}
                        Polygon
                    </label>
                </div>
            </div>

            <div id='mapDiv' style={{ height: '450px', borderRadius: '6px', overflow: 'hidden', margin: '5px' }}>
                <Map
                    center={mapCenter}
                    zoom={zoom}
                    onBoundsChanged={({ center, zoom }) => {
                        setMapCenter(center);
                        setZoom(zoom);
                    }}
                    defaultZoom={18}
                    maxZoom={19}
                    minZoom={5}
                    onClick={handleClick}
                    provider={provider}
                >
                    {satelliteView && <Overlay provider={LABELS_PROVIDER} />}

                    {Array.isArray(polygon) && polygon.map((point, i) => (
                        <Marker key={i} anchor={point} />
                    ))}

                    {geofence && (
                        <GeoJson
                            data={geofence}
                            styleCallback={() => ({
                                fill: 'rgba(11, 33, 235, 0.2)',
                                strokeWidth: 2,
                                stroke: 'blue',
                            })}
                        />
                    )}

                    <ZoomControl />
                </Map>
            </div>

            <div className='bg-light d-flex flex-column' id='mapControls'>
                <div className='d-flex mb-2'>
                    {geofenceType === 'circular' && (
                        <div className='d-flex flex-column gap-2 border-bottom pb-2'>
                            <div className='d-flex gap-2'>
                                <input
                                    className='recenterMap mapInputField'
                                    type='number'
                                    value={geofenceRadius}
                                    onChange={(e) => {
                                        const n = toNumberOrNull(e.target.value);
                                        if (n === null) return;
                                        setCircularGeofence((prev) => ({ ...(prev || {}), radius: n }));
                                    }}
                                    placeholder='Enter radius'
                                />

                                <input
                                    className='recenterMap mapInputField'
                                    type='number'
                                    value={circularGeofence?.lat ?? ''}
                                    onChange={(e) => {
                                        const n = toNumberOrNull(e.target.value);
                                        setCircularGeofence((prev) => ({ ...(prev || {}), lat: n }));
                                    }}
                                    placeholder='Enter Latitude'
                                />

                                <input
                                    className='recenterMap mapInputField'
                                    type='number'
                                    value={circularGeofence?.long ?? ''}
                                    onChange={(e) => {
                                        const n = toNumberOrNull(e.target.value);
                                        setCircularGeofence((prev) => ({ ...(prev || {}), long: n }));
                                    }}
                                    placeholder='Enter Longitude'
                                />
                            </div>

                            <div className='d-flex flex-column'>
                                <div>
                                    <input type='checkbox' checked={circularGeofence?.displayOnly ?? false} onChange={(e) => {
                                        const v = e.target.checked;
                                        setCircularGeofence((prev) => ({ ...(prev || {}), displayOnly: v }));
                                        console.log('display only on map', v);
                                    }} /> Display Only on Map
                                </div>
                                <span className='fs-6 text-muted'>Checking this will use the geofence for beacon location display purposes only</span>
                            </div>
                        </div>
                    )}

                </div>

                <div className='d-flex flex-wrap gap-2'>
                    <button className='recenterMap' onClick={handleRecenter}>
                        <FaRegCompass color={'blue'} size={Spacing.SCALE_18} /> Recenter
                    </button>

                    <button className='recenterMap' onClick={clearMap}>
                        <BsArrowCounterclockwise color={'black'} size={Spacing.SCALE_18} /> Clear Map
                    </button>

                    <button
                        className='recenterMap d-flex align-items-center gap-2'
                        style={{ minWidth: 180, whiteSpace: 'nowrap' }}
                        onClick={handleMapView}
                    >
                        {satelliteView ?
                            <FaToggleOn color={'black'} size={Spacing.SCALE_18} /> :
                            <FaToggleOff color={'black'} size={Spacing.SCALE_18} />
                        }
                        {satelliteView ? 'Turn Off Satellite View' : 'Turn On Satellite View'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LiveLocationMap;
